import { Apollo, gql } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { IdGetterObj, InMemoryCache, ApolloLink, GraphQLRequest, DefaultOptions } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import QueueLink from 'apollo-link-queue/build/dist/QueueLink';
import { buildClientSchema, GraphQLInputObjectType, GraphQLSchema } from 'graphql';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subscriber } from 'rxjs';
import { map, skipWhile, take } from 'rxjs/operators';
import { ConfigService } from 'src/app/modules/core/services/config.service';
import { LoaderService } from 'src/app/modules/core/services/loader.service';
import { NotifyOrConfirmService } from 'src/app/modules/core/services/notify-or-confirm.service';
import { LogoutAction } from '../enums/logout-action.enum';
import { NotifyOrConfirmType } from '../enums/notify-or-confirm-type.enum';
import { LoggerService } from './logger.service';
import { SessionService } from './session.service';
import { WindowRefService } from './window.service';
import { getIntrospectionQuery } from 'graphql/utilities';

@Injectable({
	providedIn: 'root'
})
export class GraphqlService {
	public static loadingSchema = false;
	public static schema$: BehaviorSubject<GraphQLSchema> = new BehaviorSubject(null);

	private _apolloDict: Map<string, Apollo>;

	constructor(
		protected http: HttpClient,
		protected httpLink: HttpLink,
		protected configService: ConfigService,
		protected loaderService: LoaderService,
		protected notifyOrConfirmService: NotifyOrConfirmService,
		protected sessionService: SessionService,
		protected translateService: TranslateService,
		protected router: Router,
		protected logger: LoggerService,
		protected ngZone: NgZone,
		protected toastrService: ToastrService,
		protected windowRef: WindowRefService
	) {
		this._apolloDict = new Map<string, Apollo>();
	}

	/**
	 * Apollo client
	 */
	protected get apollo(): Apollo {
		try {
			// Check if user is online or offline
			const offlineLink: any = this.getNewQueueLink();
			this.windowRef.nativeWindow.addEventListener('offline', () => offlineLink.close());
			this.windowRef.nativeWindow.addEventListener('online', () => offlineLink.open());

			// declare apollo client
			let apollo: Apollo;

			// get apollo by url from map
			if (this._apolloDict) {
				apollo = this._apolloDict.get(this.configService.config.apiUri);
			}

			// if apollo exists, return it
			if (apollo) {
				return apollo;
			}

			// create http link
			const httpLink = this.httpLink.create({ uri: this.configService.config.apiUri });

			// get middleware
			const middleware = setContext(this.getMiddleware.bind(this));

			// create a link with included offlineLink
			// const link = ApolloLink.from([new RetryLink(), offlineLink, middleware.concat(httpLink)]);
			const link = ApolloLink.from([offlineLink, middleware.concat(httpLink)]);

			// create default options
			const defaultOptions: DefaultOptions = {
				query: {
					fetchPolicy: 'network-only',
					errorPolicy: 'all'
				}
			};

			// create new apollo client
			apollo = new Apollo(this.ngZone);
			apollo.create({
				link,
				cache: new InMemoryCache({
					dataIdFromObject: this.getDataIdFromObject
				}),
				queryDeduplication: true,
				defaultOptions
			});

			// store it in map
			this._apolloDict.set(this.configService.config.apiUri, apollo);

			// return apollo
			return apollo;
		} catch (error) {
			// Log
			this.logger.error(error);

			// Reset
			this._apolloDict = new Map<string, Apollo>();
			GraphqlService.resetSchema();

			// Logout
			this.ngZone.run(() => {
				this.router.navigate(['/user-auth/logout/' + LogoutAction.TIMEOUT]);
			});
		}
	}

	/**
	 * Reset schema
	 */
	public static resetSchema() {
		GraphqlService.schema$.next(null);
		GraphqlService.loadingSchema = false;
	}

	/**
	 * Get new queue link
	 */
	public getNewQueueLink() {
		return new QueueLink();
	}

	/**
	 * Get data ID from object
	 *
	 * @param object
	 */
	public getDataIdFromObject(object: IdGetterObj) {
		return (object as any).uid || null;
	}

	/**
	 * Modify graphql requests before send
	 *
	 * @param operation GraphQL request
	 * @param context Request context
	 */
	async getMiddleware(operation: GraphQLRequest, context: any): Promise<any> {
		// verify context
		context = context || {};

		if (!context.headers) {
			context.headers = {};
		}

		if (!(await this.sessionService.check().toPromise())) {
			return context;
		}

		// Set headers
		context.headers['Session-Id'] = this.sessionService.session.uid;
		context.headers['Authorization'] = 'Bearer ' + this.sessionService.session.accessToken;

		if (operation.operationName !== 'IntrospectionQuery' && this.sessionService.reCaptchaCode) {
			context.headers['ReCaptchaCode'] = this.sessionService.reCaptchaCode;
			this.sessionService.reCaptchaCode = null;
		}

		// Return context
		return context;
	}

	/**
	 * Error handling
	 *
	 * @param error
	 * @param subscriber
	 */
	public errorHandling(error: any, subscriber: Subscriber<any>) {
		this.logger.error(error);
		this.loaderService.loader.stopLoading();
		if (_.get(error, 'networkError.status') === 401) {
			if (_.get(error, 'networkError.error.additionalInformation') === 'Unauthorized') {
				if (JSON.stringify(_.get(error, 'networkError.error.message', '')).includes('has expired!')) {
					this.router.navigate(['/user-auth/logout/' + LogoutAction.EXPIRED]);
					return;
				}
			}

			if (_.get(error, 'networkError.error.additionalInformation') === 'UserNotActivated') {
				this.notifyOrConfirmService.showNotification(
					this.translateService.instant('authService.userDeactivatedTitle'),
					this.translateService.instant('authService.userDeactivatedMessage'),
					NotifyOrConfirmType.ERROR,
					this.translateService.instant('authService.buttonText')
				);
				this.router.navigate(['/user-auth/logout/' + LogoutAction.SECURITY]);
				return;
			}

			switch (_.get(error, 'networkError.error.additionalInformation')) {
				// Insufficient permission or invalid claims
				case 'InsufficientPermission':
				case 'InvalidClaims':
					this.notifyOrConfirmService.showNotification(
						this.translateService.instant('authService.insufficientPermissionTitle'),
						this.translateService.instant('authService.insufficientPermissionMessage'),
						NotifyOrConfirmType.ERROR,
						this.translateService.instant('authService.buttonText')
					);
					this.router.navigate(['/user-auth/logout/' + LogoutAction.SECURITY]);
					break;

				// Invalid recaptcha
				case 'InvalidRecaptcha':
					this.notifyOrConfirmService.showNotification(
						this.translateService.instant('authService.invalidRecaptchaTitle'),
						this.translateService.instant('authService.invalidRecaptchaMessage'),
						NotifyOrConfirmType.ERROR,
						this.translateService.instant('authService.buttonText')
					);
					subscriber.error(error);
					break;

				// Invalid user state
				case 'InvalidUserState':
					this.notifyOrConfirmService.showNotification(
						this.translateService.instant('authService.userDeactivatedTitle'),
						this.translateService.instant('authService.userDeactivatedMessage'),
						NotifyOrConfirmType.ERROR,
						this.translateService.instant('authService.buttonText')
					);
					this.router.navigate(['/user-auth/logout/' + LogoutAction.SECURITY]);
					break;

				// Default
				default:
					subscriber.error(error);
			}
		} else if (subscriber) {
			subscriber.error(error);
		}
	}

	/**
	 * Executes the 'Graphql' mutation and handles errors that occur.
	 */
	public mutation(
		mutation: any,
		variables?: any,
		config?: {
			showSuccessMessage?: boolean;
			successMessage?: string;
		}
	) {
		// Process config
		config = Object.assign(
			{
				showSuccessMessage: true,
				successMessage: this.translateService.instant('graphqlService.successMessage')
			},
			config
		);

		return new Observable((subscriber) => {
			this.prepareVariables(mutation, variables)
				.then((resultVariables) => {
					if (this.apollo) {
						this.apollo
							.mutate({
								mutation,
								variables: resultVariables
							})
							.subscribe(
								(response) => {
									if (config.showSuccessMessage) {
										this.toastrService.success(config.successMessage);
									}
									subscriber.next(response.data);
									subscriber.complete();
								},
								(error) => {
									this.errorHandling(error, subscriber);
								}
							);
					}
				})
				.catch((error) => {
					this.errorHandling(error, subscriber);
				});
		});
	}

	/**
	 * Executes the 'Graphql' query and handles errors that occur
	 */
	public query(query: any, variables?: any) {
		return new Observable((subscriber) => {
			this.prepareVariables(query, variables)
				.then((resultVariables) => {
					if (this.apollo) {
						this.apollo
							.query({
								query,
								variables: resultVariables
							})
							.subscribe(
								(response) => {
									// GraphQL errors must be handled here!
									if (response.errors && response.errors.length) {
										subscriber.error(response.errors[0]);
									} else {
										subscriber.next(response.data);
									}
									subscriber.complete();
								},
								(error) => {
									this.errorHandling(error, subscriber);
								}
							);
					}
				})
				.catch((error) => {
					this.errorHandling(error, subscriber);
				});
		});
	}

	/**
	 * Prepare input variables
	 *
	 * @param action Graphql action tag
	 * @param variables Variables
	 */
	protected async prepareVariables(action: any, variables?: any) {
		if (!variables || !action || !action.definitions || action.definitions.length !== 1) {
			return variables;
		}

		// Get schema and operation properties
		try {
			const schema = await this.getSchema();
			if (!schema) {
				return variables;
			}

			const operation = action.definitions[0].operation;
			let operationName: string;

			// Use definition name as operationName (fallback)
			if (action.definitions[0].name) {
				operationName = action.definitions[0].name.value;
			}

			// Use selection name as operationName
			if (
				action.definitions[0].selectionSet &&
				action.definitions[0].selectionSet.selections &&
				action.definitions[0].selectionSet.selections.length > 0
			) {
				operationName = action.definitions[0].selectionSet.selections[0].name.value;
			}

			// Check operationName
			if (!operationName) {
				throw new Error('Missing name for ' + operation + ' in GraphQL request');
			}

			// resolve fields (functions)
			let fields = null;
			if (operation === 'query') {
				fields = schema.getQueryType().getFields();
			} else if (operation === 'mutation') {
				fields = schema.getMutationType().getFields();
			} else {
				return variables;
			}

			// get field (function)
			const field = fields[operationName];

			if (!field || !field.args || field.args.length === 0) {
				return variables;
			}

			// map variables
			const result = {};

			for (const arg of field.args) {
				const argName = arg.name;
				const schemaType = this.getSchemaType(arg);
				const value = variables[argName];

				// Check for null or undefined, but don't continue for Int with value = 0...
				if (value == null) {
					continue;
				}

				result[argName] = await this.mapToSchemaType(value, schemaType);
			}

			// Return mapped variables
			return result;
		} catch (error) {
			// ignore error to not prevent GQL request if an error occurs (no schema found)
			this.logger.error(error);
			return variables;
		}
	}

	/**
	 * Get type from schema
	 *
	 * @param type
	 */
	protected async getTypeFromSchema(type: string): Promise<GraphQLInputObjectType> {
		const gqlSchema = await this.getSchema();
		return <GraphQLInputObjectType>gqlSchema.getType(type);
	}

	/**
	 * Map input type to schema type
	 *
	 * @param source Source
	 * @param schemaType Schema type name
	 */
	protected async mapToSchemaType(source: any, schemaType: string) {
		try {
			const gqlNamedType = await this.getTypeFromSchema(schemaType);
			if (!gqlNamedType) {
				throw new Error(`mapToSchemaType: Graphql type '${schemaType}' not found.`);
			}

			if (!gqlNamedType.getFields) {
				return source;
			}

			const fields = gqlNamedType.getFields();

			let dest: any = null;

			if (Array.isArray(source)) {
				dest = [];
				source.forEach((item) => {
					const tmp = this.mapFields(fields, item);
					dest.push(tmp);
				});
			} else {
				dest = this.mapFields(fields, source);

				// Check for empty object
				if (typeof dest === 'object' && Object.keys(dest).length === 0) {
					dest = undefined;
				}
			}

			return dest;
		} catch (error) {
			this.logger.error(error);
			throw error;
		}
	}

	/**
	 * Get current schema
	 */
	public getSchema(): Promise<GraphQLSchema> {
		return new Promise<GraphQLSchema>((resolve, reject) => {
			if (!GraphqlService.loadingSchema) {
				GraphqlService.loadingSchema = true;

				this.downloadSchema().subscribe(
					() => {
						GraphqlService.schema$
							.pipe(
								skipWhile((value) => value === null),
								take(1)
							)
							.subscribe(
								(schema) => {
									resolve(schema);
								},
								(error) => {
									reject(error);
								}
							);
					},
					(error) => {
						reject(error);
					}
				);
			} else {
				GraphqlService.schema$
					.pipe(
						skipWhile((value) => value === null),
						take(1)
					)
					.subscribe(
						(schema) => {
							resolve(schema);
						},
						(error) => {
							reject(error);
						}
					);
			}
		});
	}

	/**
	 * Get graphql schema from server
	 */
	public downloadSchema(): Observable<GraphQLSchema> {
		return new Observable<GraphQLSchema>((subscriber) => {
			this.query(gql(getIntrospectionQuery()), false)
				.pipe(
					map((config: any) => {
						try {
							return buildClientSchema(config);
						} catch (e) {
							return null;
						}
					})
				)
				.subscribe(
					(schema) => {
						if (!schema) {
							this.logger.error('Missing schema');
							GraphqlService.resetSchema();
							subscriber.error();
							return;
						}
						GraphqlService.schema$.next(schema);
						subscriber.next(schema);
						subscriber.complete();
					},
					(error) => {
						this.logger.error(error);
						GraphqlService.resetSchema();
						subscriber.error(error);
					}
				);
		});
	}

	/**
	 * Get schema type
	 *
	 * @param arg Argument
	 */
	private getSchemaType(arg: any) {
		let schemaType: string = null;

		if (arg && arg.type) {
			if (arg.type.ofType) {
				schemaType = this.getSchemaTypeOfType(arg.type.ofType);
			} else if (arg.type.name) {
				schemaType = arg.type.name;
			}
		}

		return schemaType;
	}

	/**
	 * Get name from ofType
	 *
	 * @param ofType OfType
	 */
	private getSchemaTypeOfType(ofType: any) {
		if (ofType) {
			if (ofType.ofType) {
				return this.getSchemaTypeOfType(ofType.ofType);
			}

			return ofType.name;
		}

		return null;
	}

	/**
	 * Map schema fields
	 *
	 * @param dest Destination object
	 * @param fields Fields list
	 * @param source Source object
	 */
	private mapFields(fields: any, source: any, dest: any = {}) {
		Object.keys(fields).forEach((key) => {
			const value = source[key];
			if (value !== undefined) {
				dest[key] = value;
			}
		});

		return dest;
	}
}
